import { makeStyles } from '@mui/styles';
import MenuItem from 'apollo-react/components/MenuItem';
import Modal from 'apollo-react/components/Modal';
import Select from 'apollo-react/components/Select';
import TextField from 'apollo-react/components/TextField';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useTranslation } from '../custom-hooks/useTranslation';
import {
  ENTITY_TYPE_PARENT,
  ENTITY_TYPES,
} from '../entity-designer/constants/entity-designer.constants';
import {
  initialEntitySettings,
  initialErrors,
} from '../entity-designer/constants/new-entity.constants';
import { getHasSpecializedRight } from '../entity-designer/selectors/entity-designer.selectors';
import {
  getNewEntityLink,
  validateNewEntityModalOnBlur,
  validateNewEntityModalOnCreate,
} from '../entity-designer/utils/create-entity.utils';

const useStyles = makeStyles({
  modal: {
    width: 650,
  },
});

const NewEntityDialog = ({ open, handleClose }) => {
  const classes = useStyles();
  const translateString = useTranslation();
  const history = useHistory();

  const hasSpecializedRight = useSelector(getHasSpecializedRight);

  const [entitySettings, setEntitySettings] = useState(initialEntitySettings);
  const [modalErrors, setErrors] = useState(initialErrors);

  const hasModalErrors = Object.values(modalErrors).filter((item) => item.hasError).length !== 0;

  const handleEntitySettingsChange = (e) => {
    const entitySettingProps = { ...entitySettings, [e.target.name]: e.target.value };

    if (e.target.name === 'useGenericTable') {
      entitySettingProps.tableName = '';
      setErrors({ ...modalErrors, tableName: { hasError: false, errorText: '' } });
    }

    setEntitySettings({ ...entitySettingProps });
  };

  const handleEntitySettingsBlur = (e) => {
    const errors = validateNewEntityModalOnBlur({
      entitySettings,
      propName: e.target.name,
      modalErrors,
    });
    setErrors({ ...errors });
  };

  const handleCreateButtonClick = async () => {
    const errors = await validateNewEntityModalOnCreate(entitySettings);

    if (Object.values(errors).filter((item) => item.hasError).length !== 0) {
      setErrors({ ...errors });
    } else {
      handleClose();
      history.push(getNewEntityLink(entitySettings));
    }
  };

  return (
    <Modal
      disableBackdropClick
      open={open}
      variant="default"
      onClose={handleClose}
      title={translateString('Create New Entity')}
      buttonProps={[
        {
          label: translateString('Cancel'),
        },
        {
          label: translateString('Create'),
          onClick: handleCreateButtonClick,
          disabled: hasModalErrors,
        },
      ]}
      className={classes.modal}
    >
      <TextField
        label={translateString('Entity Name')}
        placeholder={translateString('Entity Name...')}
        name="entityName"
        value={entitySettings.entityName}
        onChange={handleEntitySettingsChange}
        onBlur={handleEntitySettingsBlur}
        error={modalErrors.entityName.hasError}
        helperText={
          modalErrors.entityName.hasError ? translateString(modalErrors.entityName.errorText) : null
        }
        InputProps={{
          inputProps: {
            maxLength: 25,
          },
        }}
        fullWidth
        required
      />

      <Select
        label={translateString('Parent/Child Entity')}
        value={entitySettings.entityType}
        onChange={handleEntitySettingsChange}
        name="entityType"
        canDeselect={false}
        fullWidth
        required
      >
        {ENTITY_TYPES.map(({ label, value }) => (
          <MenuItem value={value} key={`${label}-${value}`}>
            {translateString(label)}
          </MenuItem>
        ))}
      </Select>
      {hasSpecializedRight && (
        <Select
          label={translateString('Use Generic Table')}
          value={entitySettings.useGenericTable}
          onChange={handleEntitySettingsChange}
          name="useGenericTable"
          canDeselect={false}
          fullWidth
          required
        >
          <MenuItem value={true}>{translateString('Yes')}</MenuItem>
          <MenuItem value={false}>{translateString('No')}</MenuItem>
        </Select>
      )}
      {!entitySettings.useGenericTable && (
        <>
          <TextField
            label={translateString('Table Name')}
            placeholder={translateString('Table Name...')}
            name="tableName"
            value={entitySettings.tableName}
            onChange={handleEntitySettingsChange}
            onBlur={handleEntitySettingsBlur}
            error={modalErrors.tableName.hasError}
            helperText={
              modalErrors.tableName.hasError
                ? translateString(modalErrors.tableName.errorText)
                : null
            }
            fullWidth
            required
          />
          {entitySettings.entityType === ENTITY_TYPE_PARENT && (
            <TextField
              label={translateString('Table Name for Full-Text Search')}
              placeholder={translateString('Table Name for Full-Text Search...')}
              name="runtimeTable"
              value={entitySettings.runtimeTable}
              disabled
              fullWidth
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default NewEntityDialog;
